import { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Link,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { Sidebar, SidebarOpener } from '@/components/sidebar';
import { Notifications } from '@/components/notifications';
import { UserNavigation } from '@/components/user-navigation';
import { Dashboard } from '@/screens/dashboard';
import { Explore } from '@/screens/explore';
import { ExploreDev } from '@/screens/explore-dev';
import { ProductPerformance } from '@/screens/product-performance';
import { Register } from '@/screens/register';
import { Profile } from '@/screens/profile';
import { Plans } from '@/screens/plans';
import { Import } from '@/screens/import';
import { PurchasePlan } from '@/screens/purchase-plan';
import { useKeycloak } from '@react-keycloak/web';
import { PrivateRoute } from '@/utilities/private-route';
import { ProfilePlans } from '@/screens/profile-plans';
import { Desktop } from '@/screens/desktop';
import { Invoices } from '@/screens/invoices';
import { Media } from '@/screens/media';
import { useUserConfig } from '@/hooks/user/use-user-config';
import { DragDrop } from '@/screens/drag-drop';
import {
  XCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { Chain } from '@/screens/chain';
import { Preview } from '@/screens/preview';
import { FormEditor } from '@/screens/form-editor';
import { createTreeStore } from '@/tree-store';
import { useStore } from 'zustand';
import { TreeState, TreeAction } from '../../tree-store';
import { CompAnalysis } from '@/screens/comp-analysis';
import * as Sentry from '@sentry/react';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { PushNotifications } from '@/components/push-notifications';
import { Log } from '@/screens/log';
import { ChainLog } from '@/screens/chain-log';
import { Monitor } from '@/screens/monitor';
import { Downloads } from '@/screens/downloads';
import { TrumpfMetrics } from '@/screens/trumpf-metrics';
import { CustomWidget } from '@/screens/libelle-onlineshop';
import { PageEditor } from '@/screens/page-editor';
import { PuckPreviewScreen } from '@/screens/puck-preview';
import { CapabilitiesStudio } from '@/screens/capabilities-studio';
import { CodeEditor } from '@/screens/code-editor';
import { ManageContainers } from '@/screens/manage-containers';
import moment from 'moment';
import { PaymentDetails } from '@/screens/payment-details';
import { Join } from '@/screens/join';
import { AddPaymentMethod } from '@/screens/add-payment-method';
import { useMatch } from 'react-router-dom';
import { Capabilities } from '@/screens/capabilities';
import { Vault } from '@/screens/vault';
import { ManageOrganisation } from '@/screens/manage-organisation';
import { configureEchoForToken } from '@/utils/echo';

export function AppRouter() {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const { keycloak, initialized } = useKeycloak();
  const { data, refetch } = useUserConfig();
  const location = useLocation();
  const navigate = useNavigate();
  // TODO, we need to change this so we can use the context...
  const store = createTreeStore('explore-a');
  const matchRegisterStripe = useMatch('/register/:id');
  const matchRegister = useMatch('/register');
  const hideTopBar = matchRegister || matchRegisterStripe;

  const useTreeStore = <T,>(selector: (state: TreeState & TreeAction) => T) => {
    if (store === null) {
      throw new Error('No provider');
    }

    return useStore(store, selector);
  };

  const clearTreeStore = useTreeStore((state) => state.clear);

  useEffect(() => {
    keycloak.onAuthSuccess = () => {
      refetch();
      clearTreeStore();
      configureEchoForToken();

      if (location.pathname === '/register') {
        return navigate('/');
      }
    };

    keycloak.onAuthLogout = () => {
      return navigate('/');
    };
  }, []);

  if (!initialized) {
    return (
      <div className="text-center w-screen h-screen">
        <div
          role="status"
          className="flex justify-center items-center h-screen"
        >
          <svg
            aria-hidden="true"
            className="inline w-16 h-16 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  function Fallback({
    componentStack,
    error,
    resetError,
  }: {
    error: Error;
    componentStack: string;
    resetError(): void;
  }) {
    return (
      <div className="grid place-content-center h-full">
        <div className="rounded-md bg-red-50 border border-red-300 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <XCircleIcon
                className="h-5 w-5 text-red-400"
                aria-hidden="true"
              />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                There was an error rendering the page: {error.toString()}
              </h3>
              <div className="mt-2 text-[10px] text-red-700 overflow-x-auto">
                <pre>{componentStack}</pre>
              </div>
              <button
                onClick={resetError}
                type="button"
                className="mt-3 rounded-md bg-red-800 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Sidebar isOpen={sidebarOpen} onClose={setSidebarOpen} />
      <div className="flex flex-col md:pl-64">
        <div className="sticky top-0 z-10 flex h-16 flex-shrink-0 bg-gray-100  dark:bg-gray-700">
          <SidebarOpener onOpen={() => setSidebarOpen(true)} />
          {!hideTopBar && (
            <div className="flex flex-1 justify-end px-4">
              <div id="page-actions" className="flex items-center gap-3" />
              <div className="ml-4 flex items-center gap-3 md:ml-6">
                <ThemeToggle />
                {keycloak.authenticated && (
                  <>
                    <Notifications />
                    <PushNotifications />
                  </>
                )}
                <UserNavigation />
              </div>
            </div>
          )}
        </div>
        <main className="h-full">
          {data?.subscriptions?.length == 1 &&
            data?.subscriptions[0].status == 'trialing' &&
            data?.subscriptions[0].default_payment_method == null && (
              <div className="rounded-md border-yellow-400 bg-yellow-50 p-4 sm:ml-6 md:ml-8 sm:mr-6 md:mr-8 mt-2">
                <div className="flex">
                  <div className="shrink-0">
                    <ExclamationTriangleIcon
                      aria-hidden="true"
                      className="w-5 h-5 text-yellow-400"
                    />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      You are currently trialing the plan "
                      {data?.subscriptions[0].plan.product.name}" until{' '}
                      {moment
                        .unix(data?.subscriptions[0].trial_end)
                        .format('LL')}
                      .{' '}
                      <a
                        href={`/payment-details/${data?.subscriptions[0].subscription}`}
                        className="font-medium text-yellow-700 underline hover:text-yellow-600"
                      >
                        Add a payment method now to continue using this plan
                        after the trial ends.
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            )}
          <Sentry.ErrorBoundary fallback={(props) => <Fallback {...props} />}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/join/:planId" element={<Join />} />
              <Route
                path="/containers"
                element={
                  <PrivateRoute
                    roles={['developer', 'general.fe.containerManagment']}
                  >
                    <ManageContainers />
                  </PrivateRoute>
                }
              />
              <Route
                path="/capabilities"
                element={
                  <PrivateRoute roles={['developer']}>
                    <Capabilities />
                  </PrivateRoute>
                }
              />
              <Route
                path="/explore"
                element={
                  <PrivateRoute roles={['default-roles-graph']}>
                    <Explore />
                  </PrivateRoute>
                }
              />
              <Route
                path="/view-object"
                element={
                  <PrivateRoute roles={['developer', 'general.fe.jsonViewer']}>
                    <ExploreDev />
                  </PrivateRoute>
                }
              />
              <Route
                path="/product-performance"
                element={
                  <PrivateRoute roles={['product_performance', 'developer']}>
                    <ProductPerformance />
                  </PrivateRoute>
                }
              />
              {!keycloak.authenticated && (
                <>
                  <Route path="/register" element={<Register />} />
                  <Route path="/register/:priceId" element={<Register />} />
                </>
              )}
              <Route
                path="/import"
                element={
                  <PrivateRoute roles={['developer']}>
                    <Import />
                  </PrivateRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <PrivateRoute roles={['default-roles-graph']}>
                    <Profile />
                  </PrivateRoute>
                }
              />
              <Route
                path="/plans"
                element={
                  <PrivateRoute roles={['default-roles-graph']}>
                    <Plans />
                  </PrivateRoute>
                }
              />
              <Route
                path="/purchase-plan/:priceId"
                element={
                  <PrivateRoute roles={['default-roles-graph']}>
                    <PurchasePlan />
                  </PrivateRoute>
                }
              />
              <Route
                path="/payment-details/:subscriptionId"
                element={
                  <PrivateRoute roles={['default-roles-graph']}>
                    <PaymentDetails />
                  </PrivateRoute>
                }
              />
              <Route
                path="/your-plans"
                element={
                  <PrivateRoute roles={['default-roles-graph']}>
                    <ProfilePlans />
                  </PrivateRoute>
                }
              />
              <Route
                path="/invoices"
                element={
                  <PrivateRoute roles={['default-roles-graph']}>
                    <Invoices />
                  </PrivateRoute>
                }
              />
              <Route
                path="/media"
                element={
                  <PrivateRoute roles={['default-roles-graph']}>
                    <Media />
                  </PrivateRoute>
                }
              />
              <Route
                path="/drag-drop"
                element={
                  <PrivateRoute roles={['developer', 'general.fe.edit']}>
                    <DragDrop />
                  </PrivateRoute>
                }
              />
              <Route
                path="/chain"
                element={
                  <PrivateRoute roles={['developer', 'general.fe.automate']}>
                    <Chain />
                  </PrivateRoute>
                }
              />
              <Route
                path="/chain/:chainUuid"
                element={
                  <PrivateRoute roles={['default-roles-graph']}>
                    <Chain />
                  </PrivateRoute>
                }
              />
              <Route
                path="/chain/:chainUuid/:contextUuid"
                element={
                  <PrivateRoute roles={['default-roles-graph']}>
                    <Chain />
                  </PrivateRoute>
                }
              />
              <Route
                path="/preview"
                element={
                  <PrivateRoute roles={['default-roles-graph']}>
                    <Preview />
                  </PrivateRoute>
                }
              />
              <Route
                path="/puck-preview"
                element={
                  <PrivateRoute roles={['populate', 'default-roles-graph']}>
                    <PuckPreviewScreen />
                  </PrivateRoute>
                }
              />
              <Route
                path="/schemas"
                element={
                  <PrivateRoute roles={['developer', 'general.fe.schemas']}>
                    <FormEditor />
                  </PrivateRoute>
                }
              />
              <Route
                path="/comp-analysis"
                element={
                  <PrivateRoute roles={['default-roles-graph']}>
                    <CompAnalysis />
                  </PrivateRoute>
                }
              />
              <Route
                path="/log/:uuid"
                element={
                  <PrivateRoute roles={['default-roles-graph']}>
                    <Log />
                  </PrivateRoute>
                }
              />
              <Route
                path="/chain-log/:contextUuid"
                element={
                  <PrivateRoute roles={['default-roles-graph']}>
                    <ChainLog />
                  </PrivateRoute>
                }
              />
              <Route
                path="/monitor"
                element={
                  <PrivateRoute
                    roles={['developer', 'general.fe.monitorAutomation']}
                  >
                    <Monitor />
                  </PrivateRoute>
                }
              />
              <Route
                path="/downloads"
                element={
                  <PrivateRoute roles={['developer', 'general.fe.edit']}>
                    <Downloads />
                  </PrivateRoute>
                }
              />
              {/* <Route
                path="/trumpf-metrics"
                element={
                  <PrivateRoute
                    roles={['developer', 'custom.trumpf.fe.metrics']}
                  >
                    <TrumpfMetrics />
                  </PrivateRoute>
                }
              /> */}
              {data?.userConfig?.desktops.map((desktop) => (
                <Route
                  key={desktop.uuid}
                  path={'/desktop/' + desktop.uuid}
                  element={<Desktop />}
                />
              ))}
              {data?.organisationConfig?.desktops.map((desktop) => (
                <Route
                  key={desktop.uuid}
                  path={'/desktop/' + desktop.uuid}
                  element={<Desktop />}
                />
              ))}
              <Route
                path="/libelle-onlineshop"
                element={
                  <PrivateRoute
                    roles={['default-roles-graph', 'custom.libelle-onlineshop']}
                  >
                    <CustomWidget />
                  </PrivateRoute>
                }
              />
              <Route
                path="/compose-content"
                element={
                  <PrivateRoute roles={['populate']}>
                    <PageEditor />
                  </PrivateRoute>
                }
              />
              <Route
                path="/agents/*"
                element={
                  <PrivateRoute roles={['agentsStudio']}>
                    <CapabilitiesStudio />
                  </PrivateRoute>
                }
              />
              <Route
                path="/editor"
                element={
                  <PrivateRoute roles={['developer', 'general.fe.agentEditor']}>
                    <CodeEditor />
                  </PrivateRoute>
                }
              />
              <Route
                path="/billing"
                element={
                  <PrivateRoute roles={['developer', 'general.fe.billing']}>
                    <AddPaymentMethod />
                  </PrivateRoute>
                }
              />
              <Route
                path="/vault"
                element={
                  <PrivateRoute roles={['developer', 'user.vaultAccess']}>
                    <Vault />
                  </PrivateRoute>
                }
              />
              <Route
                path="/manage-organisation"
                element={
                  <PrivateRoute roles={['developer', 'general.fe.manageOrganisation']}>
                    <ManageOrganisation />
                  </PrivateRoute>
                }
              />
            </Routes>
          </Sentry.ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
